import { useState, useEffect, FormEvent } from 'react'
import { observer } from 'mobx-react-lite'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import Input from '@/components/common/Input'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { agentStore } from '@/stores/AgentStore'
import { mcpStore } from '@/stores/MCPStore'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { AgentFramework } from '@/types'
import type { AgentCreate, MCPTool, KnowledgeGraph } from '@/types'
import { WrenchIcon, CircleStackIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface CreateAgentModalProps {
  isOpen: boolean
  onClose: () => void
}

const CreateAgentModal = observer(({ isOpen, onClose }: CreateAgentModalProps) => {
  const [formData, setFormData] = useState<AgentCreate>({
    name: '',
    description: '',
    framework: AgentFramework.LANGCHAIN,
    model: 'gpt-4o', // Updated to latest GPT-4o (2025)
    temperature: 0.7,
    max_tokens: 2000,
    system_prompt: '',
    tools: [],
    configuration: {},
  })

  const [selectedTools, setSelectedTools] = useState<string[]>([])
  const [customTool, setCustomTool] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showMCPTools, setShowMCPTools] = useState(false)
  const [configJson, setConfigJson] = useState('{}')
  const [selectedGraphs, setSelectedGraphs] = useState<string[]>([])

  // Fetch MCP servers and Knowledge Graphs when modal opens
  useEffect(() => {
    if (isOpen) {
      mcpStore.fetchServers()
      knowledgeGraphStore.fetchGraphs()
    }
  }, [isOpen])

  // Framework-specific models (Updated January 2025)
  const modelsByFramework = {
    langchain: [
      // OpenAI Models (2025)
      { value: 'gpt-4o', label: 'GPT-4o (Latest)', cost: '$$' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini', cost: '$' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', cost: '$$$' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', cost: '$' },
      // Anthropic Claude Models (2025)
      { value: 'claude-sonnet-4.5', label: 'Claude Sonnet 4.5 (Latest)', cost: '$$' },
      { value: 'claude-opus-4.1', label: 'Claude Opus 4.1', cost: '$$$$' },
      { value: 'claude-haiku-4.5', label: 'Claude Haiku 4.5', cost: '$' },
    ],
    adk: [
      // Gemini 2.5 (Latest - March 2025)
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Latest)', cost: 'FREE' },
      // Gemini 2.0 (Generally Available)
      { value: 'gemini-2.0-pro-exp', label: 'Gemini 2.0 Pro (Experimental)', cost: 'FREE' },
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', cost: 'FREE' },
      { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash-Lite', cost: 'FREE' },
      // Gemini 1.5 (Stable)
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', cost: '$$' },
      { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', cost: '$' },
    ],
    crewai: [
      { value: 'gpt-4o', label: 'GPT-4o (Latest)', cost: '$$' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini', cost: '$' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', cost: '$' },
      { value: 'claude-sonnet-4.5', label: 'Claude Sonnet 4.5', cost: '$$' },
      { value: 'claude-haiku-4.5', label: 'Claude Haiku 4.5', cost: '$' },
    ],
    custom: [
      { value: 'custom-model', label: 'Custom Model', cost: '?' },
    ],
  }

  // Framework-specific tools
  const toolsByFramework = {
    langchain: [
      'calculator',
      'web_search',
      'file_reader',
      'code_executor',
      'database_query',
      'api_caller',
    ],
    adk: [
      'google_search',
      'code_execution',
      'calculator',
      'web_search',
    ],
    crewai: [
      'calculator',
      'web_search',
      'file_reader',
      'code_executor',
    ],
    custom: [
      'calculator',
      'web_search',
    ],
  }

  const availableModels = modelsByFramework[formData.framework] || modelsByFramework.langchain
  const predefinedTools = toolsByFramework[formData.framework] || toolsByFramework.langchain

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    console.log('[CreateAgentModal] handleSubmit called')
    console.log('[CreateAgentModal] formData.name:', formData.name)
    console.log('[CreateAgentModal] formData.description:', formData.description)
    console.log('[CreateAgentModal] formData.system_prompt:', formData.system_prompt)
    console.log('[CreateAgentModal] formData.system_prompt length:', formData.system_prompt?.length)

    if (!formData.name.trim()) {
      console.log('[CreateAgentModal] Validation failed: name is empty')
      toast.error('Agent name is required')
      return
    }

    if (!formData.system_prompt || !formData.system_prompt.trim()) {
      console.log('[CreateAgentModal] Validation failed: system_prompt is empty')
      toast.error('System prompt is required')
      return
    }

    console.log('[CreateAgentModal] Validation passed, creating agent...')

    try {
      // Parse configuration JSON
      let config = {}
      try {
        config = JSON.parse(configJson)
      } catch (e) {
        toast.error('Invalid configuration JSON')
        return
      }

      const agentData: AgentCreate = {
        ...formData,
        tools: selectedTools,
        configuration: {
          ...config,
          mcp_tools: selectedTools.filter(t => t.includes(':')), // MCP tools have format "server:tool"
          knowledge_graphs: selectedGraphs,
        },
      }

      console.log('[CreateAgentModal] agentData.system_prompt:', agentData.system_prompt)
      console.log('[CreateAgentModal] Full agentData:', JSON.stringify(agentData, null, 2))
      await agentStore.createAgent(agentData)
      console.log('[CreateAgentModal] Agent created successfully')
      toast.success('Agent created successfully!')
      handleClose()
    } catch (error: any) {
      console.error('[CreateAgentModal] Error creating agent:', error)
      toast.error(error.response?.data?.detail || 'Failed to create agent')
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      framework: AgentFramework.LANGCHAIN,
      model: 'gpt-4o', // Updated to latest GPT-4o (2025)
      temperature: 0.7,
      max_tokens: 2000,
      system_prompt: '',
      tools: [],
      configuration: {},
    })
    setSelectedTools([])
    setCustomTool('')
    setShowAdvanced(false)
    setShowMCPTools(false)
    setConfigJson('{}')
    setSelectedGraphs([])
    onClose()
  }

  // Handle MCP tool selection
  const handleMCPToolToggle = (serverId: string, toolName: string) => {
    const toolId = `${serverId}:${toolName}`
    setSelectedTools((prev) =>
      prev.includes(toolId) ? prev.filter((t) => t !== toolId) : [...prev, toolId]
    )
  }

  // Handle graph selection
  const handleGraphToggle = (graphId: string) => {
    setSelectedGraphs((prev) =>
      prev.includes(graphId) ? prev.filter((g) => g !== graphId) : [...prev, graphId]
    )
  }

  const handleToolToggle = (tool: string) => {
    setSelectedTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    )
  }

  const handleAddCustomTool = () => {
    if (customTool.trim() && !selectedTools.includes(customTool)) {
      setSelectedTools([...selectedTools, customTool.trim()])
      setCustomTool('')
    }
  }

  const handleFrameworkChange = (framework: AgentFramework) => {
    // Get default model for selected framework (use latest model as default)
    const defaultModel = modelsByFramework[framework]?.[0]?.value || 'gpt-4o'

    setFormData({
      ...formData,
      framework,
      model: defaultModel,
    })

    // Clear incompatible tools when switching frameworks
    const frameworkTools = toolsByFramework[framework] || []
    setSelectedTools(prev => prev.filter(tool => frameworkTools.includes(tool)))
  }

  const getCostEstimate = (model: string): string => {
    const modelInfo = availableModels.find(m => m.value === model)
    return modelInfo?.cost || '?'
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Agent"
      description="Configure your AI agent with custom settings and tools"
      size="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <Input
            label="Agent Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="My Awesome Agent"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this agent does..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Framework and Model */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Framework
            </label>
            <select
              value={formData.framework}
              onChange={(e) => handleFrameworkChange(e.target.value as AgentFramework)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="langchain">LangChain</option>
              <option value="crewai">CrewAI</option>
              <option value="adk">Google ADK</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model
              <span className="ml-2 text-xs text-gray-500">
                (Cost: {getCostEstimate(formData.model || '')})
              </span>
            </label>
            <select
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {availableModels.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label} {model.cost === 'FREE' ? '(FREE)' : `(${model.cost})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ADK Info Banner */}
        {formData.framework === 'adk' && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-600 mb-1">
                  Google ADK Features (2025)
                </h4>
                <ul className="text-xs text-blue-600 space-y-1">
                  <li>✓ Latest Gemini 2.5 Flash model (FREE)</li>
                  <li>✓ Gemini 2.0 Pro Experimental (FREE)</li>
                  <li>✓ Native Google Search integration</li>
                  <li>✓ Built-in observability with OpenTelemetry</li>
                  <li>✓ Multi-agent orchestration support</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Model Parameters */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Temperature: {formData.temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={formData.temperature}
              onChange={(e) =>
                setFormData({ ...formData, temperature: parseFloat(e.target.value) })
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Focused</span>
              <span>Creative</span>
            </div>
          </div>

          <Input
            label="Max Tokens"
            type="number"
            value={formData.max_tokens}
            onChange={(e) =>
              setFormData({ ...formData, max_tokens: parseInt(e.target.value) })
            }
            min={100}
            max={32000}
          />
        </div>

        {/* System Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            System Prompt <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.system_prompt}
            onChange={(e) =>
              setFormData({ ...formData, system_prompt: e.target.value })
            }
            placeholder="You are a helpful AI assistant that..."
            rows={4}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
          />
        </div>

        {/* Tools Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tools
          </label>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {predefinedTools.map((tool) => (
                <label
                  key={tool}
                  className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTools.includes(tool)}
                    onChange={() => handleToolToggle(tool)}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">
                    {tool.replace('_', ' ')}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={customTool}
                onChange={(e) => setCustomTool(e.target.value)}
                placeholder="Add custom tool..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddCustomTool}
              >
                Add
              </Button>
            </div>

            {selectedTools.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTools.map((tool) => (
                  <span
                    key={tool}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/15 text-primary-800"
                  >
                    {tool.includes(':') ? tool.split(':')[1] : tool}
                    <button
                      type="button"
                      onClick={() => handleToolToggle(tool)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* MCP Tools Section */}
        {mcpStore.servers.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMCPTools(!showMCPTools)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <WrenchIcon className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">MCP Server Tools</span>
                <Badge variant="gray">{mcpStore.servers.length} server{mcpStore.servers.length !== 1 ? 's' : ''}</Badge>
              </div>
              <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform ${showMCPTools ? 'rotate-180' : ''}`} />
            </button>

            {showMCPTools && (
              <div className="p-4 space-y-4 border-t border-gray-200">
                {mcpStore.servers.map((server) => (
                  <div key={server.id} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">{server.name}</span>
                      <Badge variant="gray">{server.type.toUpperCase()}</Badge>
                    </div>
                    {mcpStore.selectedServer?.id === server.id && mcpStore.selectedServer.tools ? (
                      <div className="grid grid-cols-2 gap-2">
                        {mcpStore.selectedServer.tools.map((tool: MCPTool) => (
                          <label
                            key={`${server.id}:${tool.name}`}
                            className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTools.includes(`${server.id}:${tool.name}`)}
                              onChange={() => handleMCPToolToggle(server.id, tool.name)}
                              className="rounded text-primary-600 focus:ring-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-700">{tool.name}</span>
                              {tool.description && (
                                <p className="text-xs text-gray-500 truncate">{tool.description}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => mcpStore.fetchServer(server.id)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Load tools from {server.name}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Knowledge Graph Association */}
        {knowledgeGraphStore.graphs.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CircleStackIcon className="w-4 h-4 inline mr-1" />
              Knowledge Graphs
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Associate this agent with knowledge graphs for enhanced context
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {knowledgeGraphStore.graphs.map((graph: KnowledgeGraph) => (
                <label
                  key={graph.id}
                  className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedGraphs.includes(graph.id)}
                    onChange={() => handleGraphToggle(graph.id)}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700">{graph.name}</span>
                    <p className="text-xs text-gray-500">{graph.node_count} nodes</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Advanced Configuration */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium text-gray-900">Advanced Configuration</span>
            <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {showAdvanced && (
            <div className="p-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Configuration (JSON)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Add custom configuration parameters for this agent
              </p>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                placeholder='{"custom_param": "value"}'
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={agentStore.isLoading}
          >
            Create Agent
          </Button>
        </div>
      </form>
    </Modal>
  )
})

export default CreateAgentModal
